import React, {useEffect, useRef, useCallback} from 'react';
import {List as ImmList} from 'immutable';
import type {LottieModule} from './modules/LottieSiteModule';
import {WebflowEnvironments} from '@packages/systems/core-constants';

import {reportBug} from '@packages/utilities/reporter';

import {px, url} from '@packages/systems/core/utils/WebflowCssSubset';
import {isNodeTargetedByIx2Plugin} from '@packages/shared/utils/ix2NodeUtils';
import {getCdnUrl} from '@packages/systems/core/utils/UrlUtils';
import {
  getLottieData,
  type LottieData,
} from '@packages/systems/core/utils/LottieUtils';
import {isEqual} from '@packages/utilities/memo';

import {
  withMeta,
  withSystem,
  Plugins,
  Atom,
  type EnvironmentContextType,
  type AtomRenderProps,
} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';

import {
  TText,
  TUnionWithLabels,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  useSite,
  usePreviewMode,
  useRuntimeEnvironment,
  useAsset,
} from '@packages/systems/render';

// eslint-disable-next-line webflow/module-top-level-imports
import {withLlmRender} from '@packages/utilities/llm-wfdl-renderer/withLlmRender';
import {maybeRecoverAssetIdFromUrl} from '@packages/systems/assets';
import {
  type EvaluatedNodeData,
  getArray,
  getInPojo,
  getString,
} from '@packages/utilities/data-utils';

const animationPlugin = Plugins.name('Animation');

const getWebflowInstance = (container: HTMLElement) => {
  // @ts-expect-error - TS18047 - 'container.ownerDocument.defaultView' is possibly 'null'. | TS2339 - Property 'Webflow' does not exist on type 'Window & typeof globalThis'.
  return container.ownerDocument.defaultView.Webflow;
};

/**
 * Returns a referentially stable `LottieData`. This is done to prevent
 * re-creating the Lottie instance when config does not change.
 */
const useStableLottieConfig = (
  config: LottieData | null
): LottieData | null => {
  const stableConfigRef = useRef<LottieData | null>(config);

  if (!isEqual(stableConfigRef.current, config)) {
    stableConfigRef.current = config;
  }

  return stableConfigRef.current;
};

const useLottieAnimation = (
  rawConfig: LottieData | null,
  previewMode: any,
  hasIx2: boolean
) => {
  const config = useStableLottieConfig(rawConfig);
  const containerRef = useRef<HTMLElement | undefined>(undefined);
  const instanceRef = useRef<
    ReturnType<LottieModule['createInstance']> | undefined
  >(undefined);

  const stop = useCallback(() => {
    const instance = instanceRef.current;
    if (instance) {
      instance.stop();
      const initialIx2Value = instance.ix2InitialValue;
      if (initialIx2Value != null) {
        instance.goToFrame((instance.frames * initialIx2Value) / 100);
      }
    }
  }, []);

  // Handles clean up and destroy
  useEffect(() => {
    const currentContainerRef = containerRef.current;
    if (!currentContainerRef) return;

    const webflow = getWebflowInstance(currentContainerRef);
    if (!webflow || !webflow.require) {
      const browserWindow = currentContainerRef.ownerDocument.defaultView;
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug(
        'GlobalWebflowObject',
        'Global webflow object is uninstantiated.',
        browserWindow
      );

      return;
    }

    const lottie = webflow.require('lottie');
    if (!lottie) {
      return;
    }

    instanceRef.current = containerRef.current
      ? lottie.createInstance(containerRef.current)
      : undefined;
    return () => {
      if (instanceRef.current) {
        instanceRef.current.destroy();
      }
    };
  }, []);

  // Handles having no config (no animation loaded) and now it does, eg
  // this ensures the animation instance is loaded after user just added
  // an animation element to canvas and selected the json file,
  useEffect(() => {
    const currentContainerRef = containerRef.current;
    if (!currentContainerRef) return;

    if (config) {
      stop();
      const webflow = getWebflowInstance(currentContainerRef);
      if (!webflow || !webflow.require) {
        const browserWindow = currentContainerRef.ownerDocument.defaultView;
        // Please see https://github.com/webflow/webflow/pull/39900
        // eslint-disable-next-line webflow/better-bug-reporting
        reportBug(
          'GlobalWebflowObject',
          'Global webflow object is uninstantiated.',
          browserWindow
        );

        return;
      }

      const lottie = webflow.require('lottie');
      if (!lottie) {
        return;
      }

      instanceRef.current = containerRef.current
        ? lottie.createInstance(containerRef.current)
        : undefined;
    }

    if (config == null && instanceRef.current) {
      instanceRef.current.destroy();
    }
  }, [config, stop]);

  // When entering preview mode, start the animation, and when exiting, stop
  // the animation
  useEffect(() => {
    setTimeout(() => {
      // Prevent playback when IX controls the element
      if (hasIx2) {
        return;
      }

      if (!instanceRef.current) {
        return;
      }

      if (previewMode) {
        instanceRef.current.play();
      } else {
        stop();
      }
    }, 0);
  }, [previewMode, stop, hasIx2]);

  return containerRef;
};

const useDataSrc = (animationData: LottieData | null) => {
  const runtimeEnvironment = useRuntimeEnvironment();
  const site = useSite();
  // Try to recover the asset using its id or recovered id from the src
  const asset = useAsset(
    animationData?.id || maybeRecoverAssetIdFromUrl(animationData?.src) || ''
  );

  const isSsr = runtimeEnvironment === WebflowEnvironments.SSR;

  // We give preference to the asset's cdnUrl because it is the most up to date
  // information, since URLs change if the asset is replaced on the Assets Panel.
  // But historically Lottie has relied on the src attribute - a hardcoded URL -
  // so we dafault to it to maintain backwards compatibility.
  const src = asset ? asset.cdnUrl : animationData?.src;

  if (isSsr) {
    return getCdnUrl(src ?? '', site.sslOk, site.cdnHost);
  } else {
    return src;
  }
};

const placeholderStyle = {
  minHeight: px(200),
  background: 'rgb(224, 224, 224)',
  backgroundSize: '50% 50%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '50% 50%',
} as const;

const defaultIx2Meta = {
  isIx2Node: false,
  ix2InitialStateValue: null,
} as const;

const getIx2Meta = (
  data: EvaluatedNodeData,
  elementId: string,
  environmentContext: EnvironmentContextType
): {
  isIx2Node: boolean;
  ix2InitialStateValue: number | null;
} => {
  const ix2Targets = environmentContext.get('ix2Targets');

  if (!ix2Targets) {
    return defaultIx2Meta;
  }

  const {pluginAnimationIdSet, pluginInitialStateValues} = ix2Targets;
  const styleBlockIds = ImmList(
    getArray<string>(getInPojo(data, ['styleBlockIds'])) ?? []
  );

  const isIx2Node = isNodeTargetedByIx2Plugin(
    elementId,
    pluginAnimationIdSet,
    styleBlockIds
  );

  if (!isIx2Node) {
    return defaultIx2Meta;
  }

  return {
    isIx2Node: true,
    ix2InitialStateValue: (
      pluginInitialStateValues[styleBlockIds.join('|')] ||
      pluginInitialStateValues[elementId] || {value: null}
    ).value,
  };
};

const Animation = Atom.createRender(
  withLlmRender(
    ({
      attributes,
      classes,
      data,
      elementId,
      environmentContext,
      generateAttributes,
    }: AtomRenderProps) => {
      const runtimeEnvironment = useRuntimeEnvironment();
      const animationData = getLottieData(data);
      const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
      const isSsr = runtimeEnvironment === WebflowEnvironments.SSR;
      const isExport = runtimeEnvironment === WebflowEnvironments.EXPORT;
      const previewMode = usePreviewMode();
      const {isIx2Node, ix2InitialStateValue} = getIx2Meta(
        data,
        elementId,
        environmentContext
      );
      const dataSrc = useDataSrc(animationData);

      let ref = null;

      if (!isSsr && !isExport) {
        // We're breaking the rules-of-hooks rules in this case to fix a
        // bug that only happens when publishing with a collection list
        // (regular static page using hooks worked fine) as a result of a hack
        // in ApolloClientUtils::walkTree, which walks the react tree, but is not really
        // React. Because it's not really React, this would blow up unless put inside
        // of this conditional. See https://github.com/webflow/webflow/issues/16327

        ref = useLottieAnimation(animationData, previewMode, isIx2Node);
      }

      const ix2InitialStateAttribute =
        ix2InitialStateValue != null
          ? {
              'data-ix2-initial-state': ix2InitialStateValue,
            }
          : null;

      const props = animationData
        ? {
            'data-w-id': elementId,
            'data-animation-type': animationData.type,
            'data-src': dataSrc,
            'data-loop': animationData.loop ? 1 : 0,
            'data-direction': animationData.playInReverse ? -1 : 1,
            'data-autoplay': isDesigner && !previewMode ? 0 : isIx2Node ? 0 : 1,
            'data-is-ix2-target': isIx2Node ? 1 : 0,
            'data-renderer': animationData.renderer,
            'data-default-duration': animationData.totalDuration,
            'data-duration': animationData.useBuiltInDuration
              ? 0
              : animationData.duration,
            'data-loading': animationData.loading,
            ...ix2InitialStateAttribute,
          }
        : null;

      const placeholderSrc = getString(
        getInPojo(data, ['animation', 'val', 'placeholderSrc'])
      );

      return (
        <div
          {...generateAttributes(attributes, classes)}
          // @ts-expect-error - TS2322 - Type 'MutableRefObject<undefined> | null' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
          ref={ref}
          {...props}
          style={
            animationData == null
              ? {
                  ...placeholderStyle,
                  // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
                  backgroundImage: url(placeholderSrc),
                }
              : {}
          }
        />
      );
    }
  )
);

const config = createIntegratedAtom({
  displayName: 'Lottie Animation',
  icon: {
    type: 'Component',
    value: qualify(animationPlugin, iconName('Animation')),
  },
  showEmpty: false,
  render: Animation,
  fields: {
    tag: withSystem({private: true}, TText),
    animation: withSystem(
      {
        label: 'Type',
        labelVariant: 'hidden',
      },
      withMeta(
        {
          variant: 'menu',
        },
        TUnionWithLabels({
          lottie: {
            label: 'Lottie',
            type: TTypeConstructor(
              qualify(animationPlugin, typeName('Lottie'))
            ),
          },
        })
      )
    ),
  },
});

export default config;
